import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { restaurantService } from "../services";
import defaultImage from "../assets/images/cuisines/default.jpeg";
import arrowLeft from "../assets/images/navbar/arrow-left.png";

// Aligned with mobile App/Screens/Resturant/DinningDetails.tsx (UI/mock booking flow)
const DiningDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [bookOpen, setBookOpen] = useState(false);
  const [guestCount, setGuestCount] = useState(4);
  const [selectedMealTime, setSelectedMealTime] = useState("Lunch");
  const [selectedTimeSlot, setSelectedTimeSlot] = useState("01:30 PM");
  const [selectedDate, setSelectedDate] = useState("Sat 24 Jan");

  const mealTimes = ["Breakfast", "Lunch", "Dinner"];
  const timeSlots = [
    "01:00 PM",
    "01:30 PM",
    "02:00 PM",
    "02:30 PM",
    "03:00 PM",
    "03:30 PM",
    "04:00 PM",
    "04:30 PM",
  ];
  const dates = ["Tue 20 Jan", "Wed 21 Jan", "Thu 22 Jan", "Fri 23 Jan", "Sat 24 Jan"];

  useEffect(() => {
    const fetchRestaurant = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const res: any = await restaurantService.getSingleRestaurant(id);
        setRestaurant(res.data || null);
      } catch (error) {
        console.error(error);
        toast.error("Failed to load restaurant");
      } finally {
        setLoading(false);
      }
    };
    fetchRestaurant();
  }, [id]);

  const handleBook = () => {
    toast.success(
      `Table booked for ${guestCount} · ${selectedDate} · ${selectedMealTime} ${selectedTimeSlot}`
    );
    setBookOpen(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900 mt-[72px]">
        <p className="dark:text-white">Loading...</p>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900 mt-[72px]">
        <p className="text-red-500">Restaurant not found</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900 min-h-screen mt-[7.5rem] md:mt-[72px] transition-colors">
      <div className="relative h-[240px] md:h-[320px]">
        <img
          src={restaurant.kitchenPhotos?.[0] || defaultImage}
          alt={restaurant.businessName}
          className="w-full h-full object-cover"
        />
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center"
        >
          <img src={arrowLeft} alt="back" className="w-5 h-5" />
        </button>
      </div>

      <div className="max-w-3xl mx-auto p-4 md:p-6">
        <h1 className="text-[#050505] dark:text-white font-urbanist text-[28px] font-semibold">
          {restaurant.businessName}
        </h1>
        <p className="text-[#6A6A6A] dark:text-gray-400 mt-1">
          {restaurant.location?.address}, {restaurant.location?.city}
        </p>
        <p className="text-[#6A6A6A] dark:text-gray-400 text-sm mt-1">
          {restaurant.operatingHours?.openTime} - {restaurant.operatingHours?.closeTime}
          {restaurant.acceptsTableBookings ? " · Table booking available" : ""}
        </p>

        <div className="flex gap-3 mt-6">
          <button
            onClick={() => setBookOpen(true)}
            className="flex-1 py-3 rounded-[150px] bg-[#7402A7] text-white font-semibold"
          >
            Book a Table
          </button>
          <button
            onClick={() => navigate(`/dining/pay-bill/${restaurant._id}`)}
            className="flex-1 py-3 rounded-[150px] border-2 border-[#7402A7] text-[#7402A7] font-semibold"
          >
            Pay Bill
          </button>
        </div>

        <div className="mt-8 rounded-[16px] border dark:border-gray-700 p-4">
          <h2 className="font-semibold dark:text-white mb-2">About</h2>
          <p className="text-sm text-[#6A6A6A] dark:text-gray-400">
            Experience dining at {restaurant.businessName}. Seating capacity{" "}
            {restaurant.seatingCapacity || "N/A"}.{" "}
            {restaurant.liveMusic ? "Live music available. " : ""}
            {restaurant.parkingAvailable ? "Parking available." : ""}
          </p>
        </div>
      </div>

      {bookOpen && (
        <div className="fixed inset-0 z-[1003] flex items-end md:items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setBookOpen(false)}
          />
          <div className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-t-[24px] md:rounded-[24px] p-6 z-10">
            <h3 className="text-xl font-semibold dark:text-white mb-4">
              Book a Table
            </h3>

            <p className="text-sm text-gray-500 mb-2">Date</p>
            <div className="flex gap-2 overflow-x-auto mb-4">
              {dates.map((d) => (
                <button
                  key={d}
                  onClick={() => setSelectedDate(d)}
                  className={`px-3 py-2 rounded-full text-sm whitespace-nowrap ${
                    selectedDate === d
                      ? "bg-[#7402A7] text-white"
                      : "bg-gray-100 dark:bg-gray-700 dark:text-white"
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>

            <p className="text-sm text-gray-500 mb-2">Guests</p>
            <div className="flex items-center gap-4 mb-4">
              <button
                className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700"
                onClick={() => setGuestCount((g) => Math.max(1, g - 1))}
              >
                −
              </button>
              <span className="dark:text-white font-semibold">{guestCount}</span>
              <button
                className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700"
                onClick={() => setGuestCount((g) => g + 1)}
              >
                +
              </button>
            </div>

            <p className="text-sm text-gray-500 mb-2">Meal</p>
            <div className="flex gap-2 mb-4">
              {mealTimes.map((m) => (
                <button
                  key={m}
                  onClick={() => setSelectedMealTime(m)}
                  className={`px-4 py-2 rounded-full text-sm ${
                    selectedMealTime === m
                      ? "bg-[#7402A7] text-white"
                      : "bg-gray-100 dark:bg-gray-700 dark:text-white"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>

            <p className="text-sm text-gray-500 mb-2">Time</p>
            <div className="flex flex-wrap gap-2 mb-6">
              {timeSlots.map((t) => (
                <button
                  key={t}
                  onClick={() => setSelectedTimeSlot(t)}
                  className={`px-3 py-2 rounded-full text-sm ${
                    selectedTimeSlot === t
                      ? "bg-[#7402A7] text-white"
                      : "bg-gray-100 dark:bg-gray-700 dark:text-white"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>

            <button
              onClick={handleBook}
              className="w-full py-3 rounded-[150px] bg-[#7402A7] text-white font-semibold"
            >
              Confirm Booking
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiningDetails;
